import React, { useMemo } from 'react';
import { Tabs, Table, Tag, Space, Typography, Badge } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SoundOutlined, MailOutlined, BellOutlined, HistoryOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Announcements from './Announcements';
import { EmailCenter } from './EmailCenter';
import NotificationCenter from './NotificationCenter';
import { useCondoStore } from '../store/condoStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import type { Announcement, EmailSchedule, OwnerNotification } from '../types';

const { Text } = Typography;

// ─── Sent History Tab ────────────────────────────────────────────────────────
type HistoryRow = {
  id: string;
  type: 'ANNOUNCEMENT' | 'EMAIL' | 'NOTIFICATION';
  title: string;
  sentAt: string;
  recipientCount: number;
  status: string;
};

const TYPE_CONFIG: Record<HistoryRow['type'], { color: string; label: string; icon: React.ReactNode }> = {
  ANNOUNCEMENT: { color: 'blue',   label: 'Announcement', icon: <SoundOutlined /> },
  EMAIL:        { color: 'green',  label: 'Email',        icon: <MailOutlined /> },
  NOTIFICATION: { color: 'purple', label: 'Notification', icon: <BellOutlined /> },
};

const STATUS_COLORS: Record<string, string> = {
  PUBLISHED:  'green',
  SENT:       'green',
  ACTIVE:     'blue',
  SCHEDULED:  'gold',
  ARCHIVED:   'default',
  CANCELLED:  'default',
};

const historyColumns: ColumnsType<HistoryRow> = [
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 140,
    render: (t: HistoryRow['type']) => {
      const cfg = TYPE_CONFIG[t];
      return (
        <Tag color={cfg.color} icon={cfg.icon}>
          {cfg.label}
        </Tag>
      );
    },
    filters: [
      { text: 'Announcement', value: 'ANNOUNCEMENT' },
      { text: 'Email', value: 'EMAIL' },
      { text: 'Notification', value: 'NOTIFICATION' },
    ],
    onFilter: (v, r) => r.type === v,
  },
  {
    title: 'Title / Subject',
    dataIndex: 'title',
    key: 'title',
    ellipsis: true,
  },
  {
    title: 'Recipients',
    dataIndex: 'recipientCount',
    key: 'recipients',
    width: 120,
    render: (n: number) =>
      n < 0 ? (
        <Space size={4}>
          <TeamOutlined style={{ color: '#1890ff' }} />
          <Text type="secondary">All residents</Text>
        </Space>
      ) : (
        <Text>{n} unit{n !== 1 ? 's' : ''}</Text>
      ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 110,
    render: (s: string) => (
      <Badge
        status={
          s === 'PUBLISHED' || s === 'SENT' || s === 'ACTIVE'
            ? 'success'
            : s === 'SCHEDULED'
            ? 'warning'
            : 'default'
        }
        text={
          <Tag color={STATUS_COLORS[s] ?? 'default'} style={{ margin: 0 }}>
            {s}
          </Tag>
        }
      />
    ),
  },
  {
    title: 'Sent / Created',
    dataIndex: 'sentAt',
    key: 'sentAt',
    width: 150,
    render: (d: string) =>
      d ? dayjs(d).format('MMM D, YYYY h:mm A') : <Text type="secondary">—</Text>,
    sorter: (a, b) => dayjs(a.sentAt).unix() - dayjs(b.sentAt).unix(),
    defaultSortOrder: 'descend',
  },
];

const SentHistoryTab: React.FC = () => {
  const {
    announcements = [],
    emailSchedules = [],
    notifications = [],
  } = useCondoStore() as any;

  const rows: HistoryRow[] = useMemo(() => {
    const fromAnnouncements: HistoryRow[] = (announcements as Announcement[])
      .filter(a => a.status === 'PUBLISHED')
      .map(a => ({
        id: `ann-${a.id}`,
        type: 'ANNOUNCEMENT' as const,
        title: a.title,
        sentAt: a.createdAt,
        recipientCount: a.targetAudience === 'BOARD' ? 5 : -1,
        status: 'PUBLISHED',
      }));

    const fromEmails: HistoryRow[] = (emailSchedules as EmailSchedule[]).map(e => ({
      id: `email-${e.id}`,
      type: 'EMAIL' as const,
      title: e.subject,
      sentAt: e.lastSentAt ?? e.scheduledAt ?? e.createdAt ?? '',
      recipientCount: e.recipientType === 'SPECIFIC_UNITS' && e.recipientUnitIds
        ? e.recipientUnitIds.split(',').length
        : -1,
      status: e.status ?? 'SCHEDULED',
    }));

    const fromNotifications: HistoryRow[] = (notifications as OwnerNotification[]).map(n => ({
      id: `notif-${n.id}`,
      type: 'NOTIFICATION' as const,
      title: n.title,
      sentAt: n.createdAt,
      recipientCount: n.targetType === 'ALL' ? -1 : 1,
      status: n.active ? 'ACTIVE' : 'ARCHIVED',
    }));

    return [...fromAnnouncements, ...fromEmails, ...fromNotifications].sort(
      (a, b) => dayjs(b.sentAt).unix() - dayjs(a.sentAt).unix(),
    );
  }, [announcements, emailSchedules, notifications]);

  return (
    <Table
      dataSource={rows}
      columns={historyColumns}
      rowKey="id"
      size="small"
      pagination={{ pageSize: 25, showTotal: (t) => `${t} communications` }}
      locale={{ emptyText: 'No communications sent yet.' }}
    />
  );
};

// ─── Main Hub ─────────────────────────────────────────────────────────────────
/**
 * S13 — Communications Hub
 * Unifies Announcements, Email Campaigns, and Notification Center into one
 * tabbed page. Owners see only the Announcements tab (S13.3).
 */
const CommunicationsHub: React.FC = () => {
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' ||
    user?.role === 'super_admin_condo' ||
    user?.role === 'superadmin';

  const { emailSchedules = [], notifications = [] } =
    useCondoStore() as any;

  // Badge: unread / active notification count for admins
  const activeNotifs: number = useMemo(
    () => (notifications as OwnerNotification[]).filter(n => n.active).length,
    [notifications],
  );

  // Badge: scheduled (pending send) email count
  const scheduledEmails: number = useMemo(
    () => (emailSchedules as EmailSchedule[]).filter(
      e => (e.status ?? '') === 'SCHEDULED',
    ).length,
    [emailSchedules],
  );

  const makeBadgeLabel = (
    icon: React.ReactNode,
    label: string,
    count: number,
  ) => (
    <Space size={4}>
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <Badge
          count={count}
          size="small"
          style={{ backgroundColor: '#1890ff', marginLeft: 2 }}
        />
      )}
    </Space>
  );

  const items = [
    {
      key: 'announcements',
      label: (
        <Space size={4}>
          <SoundOutlined />
          <span>Announcements</span>
        </Space>
      ),
      children: <Announcements />,
    },
    ...(isAdmin
      ? [
          {
            key: 'email',
            label: makeBadgeLabel(
              <MailOutlined />,
              'Email Campaigns',
              scheduledEmails,
            ),
            children: <EmailCenter />,
          },
          {
            key: 'notifications',
            label: makeBadgeLabel(
              <BellOutlined />,
              'Notifications',
              activeNotifs,
            ),
            children: <NotificationCenter />,
          },
          {
            key: 'history',
            label: (
              <Space size={4}>
                <HistoryOutlined />
                <span>Sent History</span>
              </Space>
            ),
            children: <SentHistoryTab />,
          },
        ]
      : []),
  ];

  return (
    <Tabs
      items={items}
      size="large"
      destroyInactiveTabPane={false}
      style={{ marginTop: -8 }}
    />
  );
};

export default CommunicationsHub;
